import moment from 'moment'

import { BASE_URL, simpleFormatDateString, startDateFormatString } from './constants'

/**
 * Login with credentials and retrieve an auth token
 * @param accountDetails
 * @returns {Promise}
 */
export function login(accountDetails) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(accountDetails)
  }

  return fetch(`${BASE_URL}/v1/auth/login`, options).then(handleRestResponse)
}

/**
 * Fetch a new token using a refreshtoken
 * @param {String} username
 * @param {String} refreshToken
 */
export function loginWithRefreshToken(username, refreshToken) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, refreshToken })
  }
  return fetch(`${BASE_URL}/v1/auth/login/refresh-token`, options).then(handleRestResponse)
}

// TODO: cleanup this temporary endpoint, only needed during migration
export function fetchTerminalStatus(port, terminal, username) {
  // passing port, terminal, and username is just for logging purposes
  return fetch(`${BASE_URL}/v1/terminalstatus?port=${port}&terminal=${terminal}&username=${username}`).then(
    handleRestResponse
  )
}

/**
 * Login with credentials and retrieve an auth token
 * @param authToken
 * @returns {Promise}
 */
export function getUserDetails(authToken) {
  const options = {
    method: 'GET',
    headers: {
      Authorization: authToken,
      'Content-Type': 'application/json'
    }
  }

  return fetch(`${BASE_URL}/v1/userProfile/current`, options).then(handleRestResponse)
}

export function getUserProfileSettings(authToken) {
  const options = {
    method: 'GET',
    headers: {
      Authorization: authToken,
      'Content-Type': 'application/json'
    }
  }

  return fetch(`${BASE_URL}/v1/userProfile/current/settings`, options).then(handleRestResponse)
}

function handleRestResponse(response) {
  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response.json())
  } else {
    const error = Object.assign(
      {},
      { ...response },
      {
        status: response.status,
        message: getErrorResponseMessage(response),
        response
      }
    )
    return Promise.reject(error)
  }
}

function handleRestResponseWithoutBody(response) {
  if (response.status >= 200 && response.status < 300) {
    return undefined
  } else {
    const error = Object.assign(
      {},
      { ...response },
      {
        status: response.status,
        message: getErrorResponseMessage(response),
        response
      }
    )
    return Promise.reject(error)
  }
}

function getErrorResponseMessage(response) {
  const message = response.message || response.description || response.error
  if (message) {
    if (message.includes('(code')) {
      return message.split('(code')[0]
    } else {
      return message
    }
  }

  return 'Something went wrong, please try again later'
}

export function fetchConfigFromBackend(terminalId, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: {
      Authorization: authToken,
      'Content-Type': 'application/json'
    }
  }

  return customFetch(`${BASE_URL}/v2/configuration/terminal?terminalId=${terminalId}`, options, handleRestResponse)
}

/**
 * Fetch terminal configuration using a provided fetch function; suitable for React Query usage
 * @param {{ terminalId: string|number, authToken: string, customFetch: Function }} params
 */
export function fetchTerminalConfiguration(params) {
  const { terminalId, authToken, customFetch } = params
  return fetchConfigFromBackend(terminalId, authToken, customFetch)
}

/**
 * Fetch terminalVisits from the backend
 * @param terminalId
 * @param from
 * @param to
 * @param authToken
 * @param customFetch
 * @returns {Promise}
 */
export function fetchTerminalVisitsFromBackend(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(`${BASE_URL}/v1/visit?terminalId=${terminalId}&from=${from}&to=${to}`, options, handleRestResponse)
}

/**
 * Update a terminal visit in the backend
 * @param terminalVisit
 * @param authToken
 * @param customFetch
 * @returns {Promise}
 */
export function updateTerminalVisitInBackendRest(terminalVisit, authToken, customFetch) {
  const options = {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify(terminalVisit)
  }

  return customFetch(`${BASE_URL}/v1/visit`, options, handleRestResponse)
}

/**
 * Insert a new terminalVisit
 * @param terminalVisit
 * @param authToken
 * @param customFetch
 * @returns {Promise}
 */
export function insertTerminalVisitInBackendRest(terminalVisit, authToken, customFetch) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify(terminalVisit)
  }

  return customFetch(`${BASE_URL}/v1/visit`, options, handleRestResponse)
}

/**
 * Fetch shipdetails from backend by mmsi
 * @param mmsi
 * @param authToken
 * @param customFetch
 * @returns {Promise}
 */
export function fetchShipDetailsFromBackend(mmsi, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/ship/${mmsi}?details=true&includeAgent=true&globalSupport=true`,
    options,
    handleRestResponse
  )
}

/**
 * Fetch shipdetails from backend by using a mmsi array
 * @param {string[]} mmsiList
 * @param {string} authToken
 * @param {function} customFetch
 * @returns {Promise}
 */
export function fetchShipsUsingAMmsilistFromBackend(mmsiList, authToken, customFetch) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify(mmsiList)
  }

  return customFetch(`${BASE_URL}/v1/ship/mmsilist?globalSupport=true`, options, handleRestResponse)
}

/**
 * Fetch shipdetails from backend by mmsi
 * @param {number[]} center
 * @param {number} radius
 * @param {string} authToken
 * @param {function} customFetch
 * @returns {Promise}
 */
export function fetchAllShipDetailsInTheRadius(center, radius, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/ship/circle?lat=${center[0]}&lon=${center[1]}&radius=${radius}&presentation=extended&includeAgent=true&globalSupport=true`,
    options,
    handleRestResponse
  )
}

/**
 * Upload xlxs planning file to the backend
 * @param uploadFile
 * @param terminalId
 * @param quayId
 * @param authToken
 * @param customFetch
 * @returns {Promise}
 */
export function uploadTerminalPlanningExcelToBackend(uploadFile, terminalId, quayId, authToken, customFetch) {
  // Add file to formdata so we can send it as multipart/formdata
  const formData = new FormData()
  formData.append('file', uploadFile)

  const options = {
    method: 'POST',
    headers: {
      Authorization: authToken
    },
    body: formData
  }

  return customFetch(`${BASE_URL}/v1/terminalVisit/importYara/${terminalId}/${quayId}`, options, handleRestResponse)
}

export function deleteTerminalVisitFromBackendRest(visitId, authToken, customFetch) {
  const options = {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(`${BASE_URL}/v2/terminalVisit/${visitId}`, options, handleRestResponseWithoutBody)
}

/**
 * Fetch berths occupation from the backend
 * @param terminalId
 * @param from
 * @param to
 * @param authToken
 * @param customFetch
 * @param period?
 * @returns {Promise}
 */
export function fetchBerthsOccupationFromBackend(terminalId, from, to, authToken, customFetch, period) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/occupation?from=${from}&to=${to}&period=${period}`,
    options,
    handleRestResponse
  )
}

function handleResponseDownloadFile(response, fileName) {
  if (response.status < 200 || response.status >= 300) {
    const error = {
      message: getErrorResponseMessage(response),
      response: response,
      status: response.status
    }
    return Promise.reject(error)
  }

  // TODO: read filename from response header 'Content-Disposition'?

  return response.blob().then(blob => {
    /** @type {HTMLAnchorElement} */
    // @ts-ignore
    const downloadTrigger = document.getElementById('downloadTrigger')
    const url = window.URL.createObjectURL(blob)

    downloadTrigger.href = url
    downloadTrigger.download = fileName
    downloadTrigger.click()

    window.URL.revokeObjectURL(url)
  })
}

/**
 * Create a export from the backend in yara excel format
 */
export function exportYaraTerminalVisitsToExcel(
  terminalId,
  from,
  to,
  shipType,
  administrativeFormat,
  authToken,
  customFetch
) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  // If all is selected don't filter any shiptypes
  const shipTypeUrl = shipType !== 'all' ? '&shipType=' + shipType : ''

  const fileName =
    'export-' +
    moment(from).format(simpleFormatDateString) +
    '-' +
    moment(to).format(simpleFormatDateString) +
    '-created-' +
    moment().format(startDateFormatString) +
    '.xlsx'

  return customFetch(
    `${BASE_URL}/v1/visit/export/yara?terminalId=${terminalId}&from=${from}&to=${to}${shipTypeUrl}&administrativeFormat=${administrativeFormat}`,
    options,
    response => handleResponseDownloadFile(response, fileName)
  )
}

/**
 * Create a export from the backend in botlek excel format
 */
export function exportBotlekTerminalVisitsToExcel(from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  const fileName =
    'export-' +
    moment(from).format(simpleFormatDateString) +
    '-' +
    moment(to).format(simpleFormatDateString) +
    '-created-' +
    moment().format(startDateFormatString) +
    '.xlsx'

  return customFetch(`${BASE_URL}/v1/visit/export/botlek?from=${from}&to=${to}`, options, response =>
    handleResponseDownloadFile(response, fileName)
  )
}

/**
 * Create a export from all visits and cargo operations details to Excel
 */
export function exportTerminalVisitsToExcel(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  const fileName =
    'terminalvisits-' +
    moment(from).format(simpleFormatDateString) +
    '-' +
    moment(to).format(simpleFormatDateString) +
    '.xlsx'

  return customFetch(`${BASE_URL}/v1/visit/export?terminalId=${terminalId}&from=${from}&to=${to}`, options, response =>
    handleResponseDownloadFile(response, fileName)
  )
}

/**
 * Update the terminal config in the backend
 * @param config
 * @param authToken
 * @param customFetch
 * @returns {Promise}
 */
export function updateTerminalConfigInBackendRest(config, authToken, customFetch) {
  const options = {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify(config)
  }

  return customFetch(`${BASE_URL}/v2/configuration/terminal`, options, handleRestResponse)
}

/**
 * Fetch terminalMaintenance from the backend
 * @param terminalId
 * @param from
 * @param to
 * @param authToken
 * @param customFetch
 * @returns {Promise}
 */
export function fetchTerminalMaintenanceFromBackend(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/maintenance?terminalId=${terminalId}&from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

/**
 * Insert a new terminalMaintenance
 * @param terminalMaintenance
 * @param authToken
 * @param customFetch
 * @returns {Promise}
 */
export function insertTerminalMaintenanceInBackendRest(terminalMaintenance, authToken, customFetch) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify(terminalMaintenance)
  }

  return customFetch(`${BASE_URL}/v1/maintenance`, options, handleRestResponse)
}

/**
 * Update a terminal maintenance in the backend
 * @param terminalMaintenance
 * @param authToken
 * @param customFetch
 * @returns {Promise}
 */
export function updateTerminalMaintenanceInBackendRest(terminalMaintenance, authToken, customFetch) {
  const options = {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify(terminalMaintenance)
  }

  return customFetch(`${BASE_URL}/v1/maintenance`, options, handleRestResponse)
}

export function deleteTerminalMaintenanseFromBackendRest(maintenanceId, authToken, customFetch) {
  const options = {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(`${BASE_URL}/v1/maintenance/${maintenanceId}`, options, handleRestResponseWithoutBody)
}

/**
 * Fetch CargoOperationsDetails from backend
 */
export function fetchCargoOperationsDetailsFromBackendRest(
  { terminalVisitId, orderId },
  cargoDetailsType,
  authToken,
  customFetch
) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/visit/${terminalVisitId}/order/${orderId}/opsDetails/${cargoDetailsType}`,
    options,
    handleRestResponse
  )
}

/**
 * Insert a new cargoOperationsDetails
 */
export function insertCargoOperationsDetailsInBackendRest(
  { terminalVisitId, orderId },
  cargoDetailsType,
  cargoOperationsDetails,
  authToken,
  customFetch
) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify(cargoOperationsDetails)
  }

  return customFetch(
    `${BASE_URL}/v1/visit/${terminalVisitId}/order/${orderId}/opsDetails/${cargoDetailsType}`,
    options,
    handleRestResponse
  )
}

/**
 * Update a cargoOperationsDetails in the backend
 */
export function updateCargoOperationsDetailsInBackendRest(
  { terminalVisitId, orderId },
  cargoDetailsType,
  cargoOperationsDetails,
  authToken,
  customFetch
) {
  const options = {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify(cargoOperationsDetails)
  }

  return customFetch(
    `${BASE_URL}/v1/visit/${terminalVisitId}/order/${orderId}/opsDetails/${cargoDetailsType}`,
    options,
    handleRestResponse
  )
}

export function deleteCargoOperationsDetailsFromBackendRest(
  { terminalVisitId, orderId },
  cargoDetailsType,
  authToken,
  customFetch
) {
  const options = {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/visit/${terminalVisitId}/order/${orderId}/opsDetails/${cargoDetailsType}`,
    options,
    handleRestResponse
  )
}

export function downloadCargoManifest(
  { terminalVisitId, orderId },
  cargoDetailsType,
  fileName,
  authToken,
  customFetch
) {
  const options = {
    method: 'GET',
    headers: { Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/visit/${terminalVisitId}/order/${orderId}/opsDetails/${cargoDetailsType}/cargoManifest`,
    options,
    response => handleResponseDownloadFile(response, fileName)
  )
}

export function downloadCargoManifestTemplate(terminalId, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { Authorization: authToken }
  }

  const fileName = 'cargo_manifest_template.docx'

  return customFetch(`${BASE_URL}/v1/terminal/${terminalId}/cargoManifestTemplate`, options, response =>
    handleResponseDownloadFile(response, fileName)
  )
}

/**
 * Upload .docx file with cargo manifest template
 */
export function uploadFile(url, formField, uploadFile, authToken, customFetch) {
  // Add file to formdata so we can send it as multipart/formdata
  const formData = new FormData()
  formData.append(formField, uploadFile)

  const options = {
    method: 'PUT',
    headers: {
      Authorization: authToken
    },
    body: formData
  }

  return customFetch(url, options, handleRestResponseWithoutBody)
}

export function sendCargoOperationsEmail(
  { terminalVisitId, orderId },
  cargoDetailsType,
  mailReceivers,
  replyToEmail,
  messageVariables,
  authToken,
  customFetch
) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify({
      mailReceivers,
      replyToEmail,
      messageVariables
    })
  }

  return customFetch(
    `${BASE_URL}/v1/visit/${terminalVisitId}/order/${orderId}/opsDetails/${cargoDetailsType}/summaryEmail`,
    options,
    handleRestResponseWithoutBody
  )
}

/**
 * Fetches working hours KPI data from the backend
 **/
export function fetchKpiWorkingHours(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/working-hours?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

export function fetchKpiShipWeightLossDiff(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/cargo-operations-details/total-weight-diff-per-ship?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

export function fetchKpiProductWeightLossDiff(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/cargo-operations-details/total-weight-diff-per-product?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

export function fetchKpiOrderDurationAdherence(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/orders/duration/planned-realized-diff?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

export function fetchKpiOrderStartTimeAdherence(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/orders/start-time/planned-realized-diff?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

export function fetchProductDelays(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }
  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/product/avg-delays?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

export function fetchProductAmountDiff(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }
  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/product-amounts/avg-diff-planned-actual?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

export function fetchDemurragePerProduct(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }
  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/product/demurrage?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

export function fetchDemurragePerVessel(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }
  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/vessel/demurrage?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

/**
 * Fetches load station occupation KPI data from the backend
 *
 **/
export function fetchKpiLoadStationOccupation(terminalId, from, to, authToken, customFetch, period) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  const periodParam = period ? `&period=${period}` : ''
  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/occupation/load-station?from=${from}&to=${to}${periodParam}`,
    options,
    handleRestResponse
  )
}

/**
 * Fetches data for lay-by berth occupation
 */
export function fetchLaybyBerthOccupationData(terminalId, from, to, authToken, customFetch, period) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/occupation/lay-by?from=${from}&to=${to}&period=${period}`,
    options,
    handleRestResponse
  )
}

/**
 * Fetches average discharge speed KPI data from the backend
 */
export function fetchKpiDischargeSpeed(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/product-avg-speed?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

/**
 * Fetches average spacing KPI data fom the backend
 */
export function fetchAverageSpacing(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/occupation/avg-spacing?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

/**
 * Fetches product amount KPI data from the backend
 */
export function fetchKpiProductAmounts(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/product-amounts/planned?from=${from}&to=${to}`,
    options,
    handleRestResponse
  )
}

/**
 * Fetches average delay KPI data from backend
 */
export function fetchOccupationDelay(terminalId, from, to, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/kpi/occupation/avg-delays?from=${from}&to=${to}&terminalId=${terminalId}`,
    options,
    handleRestResponse
  )
}

/**
 * Fetch a list of ships from the backend that matches a given query
 * @param {String} _name
 * @param {String} _mmsi
 * @param {String} _imo
 * @param {String} _eni
 * @param {Boolean} _globalSupport
 * @param {String} authToken
 * @param {Function} customFetch f(url, options)
 * @param {Number} limit
 * @param {boolean} onlySeaVessels
 * @returns {Promise}
 */
export function fetchShipsByQueryFromBackend(
  _name,
  _mmsi,
  _imo,
  _eni,
  _globalSupport,
  authToken,
  customFetch,
  limit,
  onlySeaVessels
) {
  const fetchFunction = customFetch || fetch
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  const name = _name ? _name : ''
  const mmsi = _mmsi ? _mmsi : ''
  const imo = _imo ? _imo : ''
  const eni = _eni ? _eni : ''
  const globalSupport = _globalSupport ? _globalSupport : false
  const maxRecords = 20

  return fetchFunction(
    `${BASE_URL}/v1/ship/search/query?name=${name}&mmsi=${mmsi}&imo=${imo}&eni=${eni}&globalSupport=${globalSupport}&maxRecords=${maxRecords}&limit=${
      limit || 20
    }&onlySeaVessels=${onlySeaVessels || false}`,
    options,
    handleRestResponse
  )
}

/**
 * Marks a ship as favorite on the backend via its mssi.
 * @param {string} mmsi ship mmsi
 *  * @param {string} terminalId id of terminal user is currently logged into
 * @param {string} authToken valid access token
 * @param {function} customFetch
 * @returns {Promise}
 */
export function addFavoriteShip(mmsi, terminalId, authToken, customFetch) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: authToken },
    body: JSON.stringify({ mmsi })
  }

  return customFetch(`${BASE_URL}/v1/terminal/${terminalId}/ship-favorite`, options, handleRestResponse)
}

/**
 * Unmarks a ship as favorite on the backend
 * @param {string} mmsi ship mmsi
 * @param {string} terminalId id of terminal user is currently logged into
 * @param {string} authToken valid access token
 * @param {function} customFetch
 * @returns {Promise}
 */
export function removeFavoriteShip(mmsi, terminalId, authToken, customFetch) {
  const options = {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }
  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/ship-favorite/${mmsi}`,
    options,
    handleRestResponseWithoutBody
  )
}

/**
 * Searches for a favorite vessel on the backend
 * @param terminalId
 * @param name
 * @param mmsi
 * @param imo
 * @param eni
 * @param authToken
 * @param customFetch
 * @returns {Promise<*|PromiseLike<*>|Promise<*>|void>}
 */
export async function searchFavoriteShips(terminalId, name, mmsi, imo, eni, authToken, customFetch) {
  const options = {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: authToken }
  }

  // filter on name, mmsi, imo or eni, in descending order of preference
  return customFetch(
    `${BASE_URL}/v1/terminal/${terminalId}/ship-favorite/search?term=${name || mmsi || imo || eni}&globalSupport=true`,
    options,
    handleRestResponse
  )
}
